/**
 * Apparitions : entrée au chargement pour ce qui est déjà à l’écran (hero compris),
 * puis au scroll pour le reste.
 * IntersectionObserver unique, opacité + translate uniquement.
 * La photo de fond du hero et le portrait restent opaques (seuls ils se décalent).
 */

const GROUP_SELECTOR = [
  '#main .section',
  '#main .home-about',
  '#main .home-hero',
  '#main .page-hero',
  '#main .photo-hero',
  '#main .yoga-do-hero',
  '#main .entry-content > .wp-block-group',
  '#main .entry-content > .wp-block-columns',
  '#main .entry-content > .wp-block-media-text',
  '#main .entry-content > .wp-block-cover',
  '#main .entry-content > .wp-block-image',
  '#main .entry-content > .wp-block-quote',
  '#main .entry-content > .wp-block-gallery',
  '.site-footer__grid',
].join(',');

const SKIP_SELECTOR = '.wp-block-separator, .wp-block-spacer, .section-divider';

const HERO_SELECTOR = '.home-hero, .page-hero, .photo-hero, .yoga-do-hero';

const HERO_PIECE_SELECTOR = [
  '.home-hero__line',
  '.home-hero__text',
  '.home-hero__figure',
  '.btn-group',
  '.page-hero__text',
  '.page-hero__figure',
  '.photo-hero__content',
  '.yoga-do-hero__content',
].join(',');

const PIECE_SELECTOR = [
  '.pilier-card',
  '.testimonial-card',
  '.news-card',
  '.booking-card',
  '.product-card',
  '.home-about__visual',
  '.home-about__body',
  '.contact-info',
  '.contact-form-wrap',
  '.practice-block__visual',
  '.practice-block__body',
  '.site-footer__grid > div',
].join(',');

const MAX_STAGGER = 8;
const MAX_DELAY_INDEX = 6;

function isSkipped(el) {
  return el.matches(SKIP_SELECTOR) || Boolean(el.closest(SKIP_SELECTOR));
}

function topLevel(group, selector) {
  const nodes = [...group.querySelectorAll(selector)];
  return nodes.filter((el) => !nodes.some((other) => other !== el && other.contains(el)));
}

function heroTargets(group) {
  const items = topLevel(group, HERO_PIECE_SELECTOR);
  const figures = items.filter((el) => el.matches('.home-hero__figure, .page-hero__figure'));
  const rest = items.filter((el) => !figures.includes(el));
  const ordered = [...figures, ...rest];
  if (ordered.length) {
    return ordered;
  }
  return [...group.querySelectorAll(':scope > .container')];
}

function collectGroups() {
  const candidates = [...document.querySelectorAll(GROUP_SELECTOR)].filter((el) => !isSkipped(el));
  const groups = candidates.filter((el) => !candidates.some((other) => other !== el && other.contains(el)));

  document.querySelectorAll('#main .entry-content > *').forEach((el) => {
    if (isSkipped(el)) {
      return;
    }
    if (groups.some((group) => group === el || group.contains(el) || el.contains(group))) {
      return;
    }
    groups.push(el);
  });

  return groups;
}

function armPiece(el, index) {
  el.style.setProperty('--reveal-i', String(Math.min(index, MAX_DELAY_INDEX)));
  el.classList.add('is-reveal');
}

function prepare(group) {
  if (group.matches(HERO_SELECTOR)) {
    const targets = heroTargets(group);
    if (targets.length) {
      group.classList.add('is-reveal-group');
      targets.forEach(armPiece);
      return;
    }
  }

  const pieces = topLevel(group, PIECE_SELECTOR);
  const header = group.querySelector(':scope > .container > .section__header, :scope > .container > header');
  const items = [];

  if (header) {
    items.push(header);
  }
  pieces.forEach((piece) => {
    if (piece !== header && !(header && header.contains(piece))) {
      items.push(piece);
    }
  });

  if (items.length >= 2 && items.length <= MAX_STAGGER) {
    group.classList.add('is-reveal-group');
    items.forEach(armPiece);
    return;
  }

  armPiece(group, 0);
}

export default function initReveal() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }
  if (!('IntersectionObserver' in window)) {
    return;
  }

  const groups = collectGroups();
  if (!groups.length) {
    return;
  }

  const viewH = window.innerHeight || document.documentElement.clientHeight;
  const intro = [];
  const below = [];

  groups.forEach((group) => {
    const rect = group.getBoundingClientRect();
    if (rect.height <= 0 || rect.bottom <= 0) {
      return;
    }
    if (rect.top < viewH * 0.72) {
      intro.push(group);
      return;
    }
    below.push(group);
  });

  if (!intro.length && !below.length) {
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) {
        return;
      }
      entry.target.classList.add('is-revealed');
      observer.unobserve(entry.target);
    });
  }, {
    root: null,
    rootMargin: '0px 0px -8% 0px',
    threshold: 0,
  });

  intro.forEach((group) => {
    prepare(group);
    group.classList.add('is-reveal-intro');
  });
  requestAnimationFrame(() => {
    intro.forEach((group) => group.classList.add('is-revealed'));
  });

  below.forEach((group) => {
    prepare(group);
    observer.observe(group);
  });

  document.addEventListener('focusin', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }
    const group = target.closest('.is-reveal-group') || target.closest('.is-reveal');
    if (!group || group.classList.contains('is-revealed')) {
      return;
    }
    group.classList.add('is-revealed');
    observer.unobserve(group);
  });

  document.addEventListener('animationend', (event) => {
    const el = event.target;
    if (!(el instanceof Element) || (event.animationName !== 'kobold-reveal' && event.animationName !== 'kobold-reveal-shift')) {
      return;
    }
    el.classList.add('is-reveal-done');
  });
}
